import { type ComponentProps, type ReactElement } from 'react';

import { type SvgIconComponent } from '@mui/icons-material';
import { type Theme } from '@mui/material/styles';

import { type MuiThemeCallbackOrValue } from '../../types/mui/sx';
import { type QuantitativeSize, type QuantitativeSizeString } from '../../types/sizes';
import { type Color } from '../../util/colors';

/**
 * @deprecated Deprecated in favor of the IconAvatar component.
 */
export enum IconVariant {
  Bare = 'Bare',
  Contained = 'Contained',
}

export type IconProp = ReactElement<IconProps> | SvgIconComponent;

/**
 * Represents the dimension, {@link IconDimension}, over which the Icon should size itself.
 */
export type IconDimension = 'height' | 'width';

export enum IconDiscreteSize {
  Large = 'large',
  Medium = 'medium',
  Small = 'small',
  XLarge = 'xlarge',
  XSmall = 'xsmall',
}

export const isIconDiscreteSize = (size: unknown): size is IconDiscreteSize =>
  Object.values(IconDiscreteSize).includes(size as IconDiscreteSize);

export const IconDiscreteSizes: Record<IconDiscreteSize, QuantitativeSizeString<'px'>> = {
  [IconDiscreteSize.Large]: '28px',
  [IconDiscreteSize.Medium]: '24px',
  [IconDiscreteSize.Small]: '20px',
  [IconDiscreteSize.XLarge]: '32px',
  [IconDiscreteSize.XSmall]: '16px',
};

/**
 * Represents the specification of an Icon's size.  An Icon can be sized either in terms of an
 * enumerated size, {@link IconDiscreteSize}, or a more flexible quantitative size,
 * {@link QuantitativeSize}.
 *
 * @see {IconDiscreteSize}
 * @see {IconDiscreteSizes}
 *
 * The {@link Icon} component sizes itself based on traditional size constraints on the height or
 * width, rather than a font size.
 */
export type IconSize = IconDiscreteSize | QuantitativeSize;

export const DefaultIconDimension: IconDimension = 'height';
export const DefaultIconSize: IconSize = IconDiscreteSize.Medium;

export const IconSizeConstraintCSSProperties = [
  'maxHeight',
  'minHeight',
  'minWidth',
  'maxWidth',
] as const;

export type IconSizeConstraintCssProperty = (typeof IconSizeConstraintCSSProperties)[number];

export type IconSizeConstraintCssValue = QuantitativeSizeString<'%' | 'px' | 'rem'>;

export type IconSizeConstraints = Partial<
  Record<IconSizeConstraintCssProperty, IconSizeConstraintCssValue>
>;

/**
 * Defines the parameters of an Icon that are used to determine it's styling.
 */
export type IconStyleParams = {
  readonly color?: 'inherit' | ((t: Theme) => string) | Color;
  /**
   * The dimension, {@link IconDimension}, over which the Icon should size itself.
   *
   * When sizing an Icon, it is more useful to specify the size in terms of the overall Icon's
   * height or width, rather than a font size.  This is because it allows easier and more consistent
   * control of the Icon's size relative to other elements, such as text, or other Icon(s).
   *
   * However, it is important that the Icon's size only be controlled in 1 dimension,
   * {@link IconDimension}, not both.  This allows the Icon to size itself alongside a given axis
   * (horizontal or vertical) while still maintaining it's aspect ratio to prevent distortion.
   *
   * The axis that the underlying svg sizes itself on is determined by this parameter, the
   * dimension, {@link IconDimension}.
   *
   * Default: "height"
   */
  readonly dimension?: IconDimension;
  /**
   * The size, {@link IconSize}, of the Icon.
   *
   * The Icon component will size itself along the axis associated with it's dimension,
   * {@link IconDimension} based on this value.
   *
   * For instance, if the 'size' is '20px', and the 'dimension' is 'height', the Icon will have
   * a height of '20px' and it's width will be set automatically based on the underlying svg's
   * aspect ratio.
   *
   * Default: "medium"
   */
  readonly size?: IconSize;
} & IconSizeConstraints;

/**
 * Represents the underlying props for an underlying SVG icon element.
 */
export type NativeIconProps = Omit<
  ComponentProps<'svg'>,
  'children' | 'color' | 'fontSize' | 'shapeRendering'
>;

export type IconProps = {
  /**
   * The background color of the Icon's container element when the variant is
   * {@link IconVariant.Contained}.  This prop is not applicable when the variant is not
   * {@link IconVariant.Contained}.
   *
   * @deprecated Deprecated in favor of the IconAvatar component.
   *
   * @default "background.paper"
   */
  readonly backgroundColor?: MuiThemeCallbackOrValue<Color>;
  /**
   * Padding that applies to the Icon's container element when the variant is
   * {@link IconVariant.Contained}.  This padding determines the spacing between the edges of the
   * Icon's colored container and the edges of the underlying SVG itself.
   *
   * This prop is not applicable when the variant is not {@link IconVariant.Contained}.
   *
   * @default 4px
   */
  readonly padding?: QuantitativeSize;
  /**
   * The variant, {@link IconVariant}, of the Icon, which controls how the Icon is rendered and
   * displayed.
   *
   * @deprecated Deprecated in favor of the IconAvatar component.
   */
  readonly variant?: IconVariant;
} & IconStyleParams &
  NativeIconProps;
